//Game Tiles & Tree Rendering
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { GAME_CONFIG, ASSET_PATHS } from '../../utils/gameConfig';
import { GameTile, TileType, Tree } from '../../types';
import Avatar from './Avatar';
import { Animal } from '../../types';
import { getTileType } from '../../services/gameLogic';
import GhostUsers from './GhostUsers';
import { UserPresence } from '../../types';
import { getColorValue } from '../../utils/colors';

interface GameGridProps {
  viewportX: number;
  viewportY: number;
  playerX: number;
  playerY: number;
  playerDirection: 'up' | 'down' | 'left' | 'right';
  playerAnimal: Animal;
  isPlayerMoving: boolean;
  trees: Tree[];
  userPreferredColor?: string;
  ghostUsers: UserPresence[];
  onContainerSizeChange?: (size: { width: number; height: number }) => void;
}

const GameGrid: React.FC<GameGridProps> = ({
  viewportX, 
  viewportY,
  playerX,
  playerY, 
  playerDirection,
  playerAnimal,
  isPlayerMoving,
  trees,
  ghostUsers,
  userPreferredColor,
  onContainerSizeChange
  }) => {

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  //Get container size
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newSize = { width: rect.width, height: rect.height };
        setContainerSize(newSize);
        onContainerSizeChange?.(newSize);
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    
    //ResizeObserver-> updates
    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateSize);
      resizeObserver.disconnect();
    };
  }, [onContainerSizeChange]);

  //Calculate dynamic viewport size based on container
  const viewport = useMemo(() => {
    //Max complete tiles
    const maxTilesWide = Math.floor(containerSize.width / GAME_CONFIG.TILE_SIZE);
    const maxTilesHigh = Math.floor(containerSize.height / GAME_CONFIG.TILE_SIZE);

    console.log('Viewport calculation:', {
      containerWidth: containerSize.width,
      containerHeight: containerSize.height,
      tileSize: GAME_CONFIG.TILE_SIZE,
      maxTilesWide,
      maxTilesHigh
    });
    
    //Guarantee at least one tile in each dimension
    const tilesWide = Math.max(5, maxTilesWide);
    const tilesHigh = Math.max(5, maxTilesHigh);
    
    //Guarantee no partial tiles
    const pixelWidth = tilesWide * GAME_CONFIG.TILE_SIZE;
    const pixelHeight = tilesHigh * GAME_CONFIG.TILE_SIZE;
    
    //Calculate centering offsets
    const offsetX = (containerSize.width - pixelWidth) / 2;
    const offsetY = (containerSize.height - pixelHeight) / 2;
    
    return {
      width: tilesWide,
      height: tilesHigh,
      pixelWidth,
      pixelHeight,
      offsetX,
      offsetY,
    };
  }, [containerSize]);

  //Tile Map
  const tileMap = useMemo(() => {
    console.log('GENERATING tile map (first time)...');
    const tiles: GameTile[][] = [];
    
    for (let y = 0; y < GAME_CONFIG.GRID_HEIGHT; y++) {
      tiles[y] = [];
      for (let x = 0; x < GAME_CONFIG.GRID_WIDTH; x++) {
        let tileType: TileType;
        let imagePath: string;
        
        //Border tiles
        if (x === 0 || x === GAME_CONFIG.GRID_WIDTH - 1 || 
            y === 0 || y === GAME_CONFIG.GRID_HEIGHT - 1) {
          tileType = 'border';
          const borderImages = ASSET_PATHS.TILES.BORDER;
          imagePath = borderImages[Math.floor(Math.random() * borderImages.length)];
        }
        //Walkable border
        else if (x === 1 || x === GAME_CONFIG.GRID_WIDTH - 2 || 
                y === 1 || y === GAME_CONFIG.GRID_HEIGHT - 2) {
          tileType = 'walkable';
          const walkableImages = ASSET_PATHS.TILES.WALKABLE;
          imagePath = walkableImages[Math.floor(Math.random() * walkableImages.length)];
        }
        //Inner space
        else {
          const adjustedX = x - 2; //Border zone
          const adjustedY = y - 2; //Border zone (2 tiles)
          
          //Pattern
          const clusterX = adjustedX % 4;
          const clusterY = adjustedY % 4;
          
          const isPlantableX = clusterX >= 1 && clusterX <= 3;
          const isPlantableY = clusterY >= 1 && clusterY <= 3; //Plantable positions (1, 2, 3)
          
          if (isPlantableX && isPlantableY) {
            tileType = 'plantable';
            imagePath = ASSET_PATHS.TILES.PLANTABLE;
          } else {
            tileType = 'walkable';
            const walkableImages = ASSET_PATHS.TILES.WALKABLE;
            imagePath = walkableImages[Math.floor(Math.random() * walkableImages.length)];
          }
          
          if (x >= 2 && x <= 12 && y >= 2 && y <= 7) {
            console.log(`Tile (${x},${y}): adjustedX=${adjustedX}, adjustedY=${adjustedY}, clusterX=${clusterX}, clusterY=${clusterY}, type=${tileType}`);
          }
        }
        
        tiles[y][x] = { x, y, type: tileType, imagePath, hasTree: false };
      }
    }
    
    console.log(`Generated ${GAME_CONFIG.GRID_WIDTH}×${GAME_CONFIG.GRID_HEIGHT} tile map`);
    return tiles;
  }, []);

  //Calculate dynamic viewport
  const visibleTiles = useMemo(() => {
    //NOTE:  Viewport pos is top left of visible area
    //NOTE: Offset calculate in forest view
    const startX = Math.max(0, viewportX);
    const startY = Math.max(0, viewportY);
    const endX = Math.min(GAME_CONFIG.GRID_WIDTH - 1, startX + viewport.width - 1);
    const endY = Math.min(GAME_CONFIG.GRID_HEIGHT - 1, startY + viewport.height - 1);
    
    const visible: GameTile[] = [];
    for (let y = startY; y <= endY; y++) {
      for (let x = startX; x <= endX; x++) {
        visible.push(tileMap[y][x]);
      }
    }
    
    return {
      tiles: visible,
      startX,
      startY,
      endX,
      endY,
      offsetX: 0,
      offsetY: 0,
    };
  }, [tileMap, viewportX, viewportY, viewport]);

  //Get visible trees
  const visibleTrees = useMemo(() => {
    return trees.filter(tree => {
      const { x, y } = tree.position;
      return x >= visibleTiles.startX && x <= visibleTiles.endX &&
             y >= visibleTiles.startY && y <= visibleTiles.endY;
    });
  }, [trees, visibleTiles]);

  //Helper function to get tree img based on stage
  const getTreeSprite = (maturityStage: number): string => {
    switch (maturityStage) {
      case 0: return ASSET_PATHS.TREES.SEED;
      case 1: return ASSET_PATHS.TREES.SPROUT;
      case 2: return ASSET_PATHS.TREES.SEEDLING;
      case 3: return ASSET_PATHS.TREES.SAPLING;
      case 4: return ASSET_PATHS.TREES.YOUNG;
      case 5: return ASSET_PATHS.TREES.MATURE;
      default: return ASSET_PATHS.TREES.MATURE;
    }
  };

  //Debugging output
    console.log('GameGrid viewport info:', {
      viewportX,
      viewportY,
      viewport: {
        width: viewport.width,
        height: viewport.height,
        pixelWidth: viewport.pixelWidth,
        pixelHeight: viewport.pixelHeight
      },
      visibleRange: {
        startX: visibleTiles.startX,
        endX: visibleTiles.endX,
        startY: visibleTiles.startY,
        endY: visibleTiles.endY
      },
      treesTotal: trees.length,
      treesVisible: visibleTrees.length
    });
  
        console.log('Avatar positioning debug:', {
        playerWorldPos: { x: playerX, y: playerY },
        visibleTiles: {
          startX: visibleTiles.startX,
          startY: visibleTiles.startY,
          offsetX: visibleTiles.offsetX,
          offsetY: visibleTiles.offsetY
        },
        calculatedAvatarPos: {
          left: (playerX - visibleTiles.startX) * GAME_CONFIG.TILE_SIZE - visibleTiles.offsetX * GAME_CONFIG.TILE_SIZE,
          top: (playerY - visibleTiles.startY) * GAME_CONFIG.TILE_SIZE - visibleTiles.offsetY * GAME_CONFIG.TILE_SIZE
        },
        relativeTilePos: {
          x: playerX - visibleTiles.startX,
          y: playerY - visibleTiles.startY
        }
      });

  return (
    <div 
      ref={containerRef}
      className="w-full h-full relative overflow-hidden bg-green-900"
    >
      {/* Center the viewport within the container */}
      <div
        className="absolute"
        style={{
          left: viewport.offsetX,
          top: viewport.offsetY,
          width: viewport.pixelWidth,
          height: viewport.pixelHeight,
        }}
      >
        {/* Tile Grid */}
        <div 
          className="absolute"
          style={{
            transform: `translate(${-visibleTiles.offsetX * GAME_CONFIG.TILE_SIZE}px, ${-visibleTiles.offsetY * GAME_CONFIG.TILE_SIZE}px)`,
          }}
        >
          {/* BACKGROUND TILES */}
          {visibleTiles.tiles.map((tile) => (
            <div
              key={`tile-${tile.x}-${tile.y}`}
              className="absolute"
              style={{
                left: (tile.x - visibleTiles.startX) * GAME_CONFIG.TILE_SIZE,
                top: (tile.y - visibleTiles.startY) * GAME_CONFIG.TILE_SIZE,
                width: GAME_CONFIG.TILE_SIZE,
                height: GAME_CONFIG.TILE_SIZE,
              }}
            >
              <img
                src={tile.imagePath}
                alt={`${tile.type} tile`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.style.backgroundColor = getTileColor(tile.type);
                }}
              />
            </div>
          ))}

          {/* Layer 2: GHOST USERS - Above tiles, below trees */}
          <GhostUsers
            ghostUsers={ghostUsers}
            viewportStartX={visibleTiles.startX}
            viewportStartY={visibleTiles.startY}
            viewportEndX={visibleTiles.endX}
            viewportEndY={visibleTiles.endY}
          />


          {/* ADD: TREE SPRITES - Render on top of tiles */}
          {visibleTrees.map((tree) => (
            <div
              key={`tree-${tree.id}`}
              className="absolute pointer-events-none z-10"
              style={{
                left: (tree.position.x - visibleTiles.startX) * GAME_CONFIG.TILE_SIZE,
                top: (tree.position.y - visibleTiles.startY) * GAME_CONFIG.TILE_SIZE,
                width: GAME_CONFIG.TILE_SIZE,
                height: GAME_CONFIG.TILE_SIZE,
              }}
            >
              {/* Tree container with colored border */}
              <div 
                className="relative w-full h-full rounded-md"
                style={{
                  border: tree.chosenColor 
                    ? `3px solid ${tree.chosenColor}` 
                    : '3px solid transparent',
                  boxShadow: tree.chosenColor 
                    ? `0 0 6px ${tree.chosenColor}40` 
                    : 'none'
                }}
              >
                <img
                  src={getTreeSprite(tree.maturityStage)}
                  alt={`${tree.planterName}'s tree (stage ${tree.maturityStage})`}
                  className="w-full h-full object-cover rounded-sm"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.style.backgroundColor = getTreeFallbackColor(tree.maturityStage);
                    target.parentElement!.style.borderRadius = '4px';
                  }}
                />
                
                {/* Optional: Tree info tooltip on hover */}
                <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-black/60 text-white text-xs p-1 rounded pointer-events-auto">
                  <div>{tree.planterName}</div>
                  <div>Stage: {tree.maturityStage}/5</div>
                  <div>Water: {tree.waterReceived}</div>
                  {tree.chosenColor && (
                    <div>Color: {tree.chosenColor}</div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Player Avatar */}
          {/* Only render avatar if player is in visible area */}
          <Avatar
            key={`player-avatar`}
            x={playerX - visibleTiles.startX}
            y={playerY - visibleTiles.startY}
            direction={playerDirection}
            animal={playerAnimal}
            isMoving={isPlayerMoving}
            preferredColor={userPreferredColor}
          />
        </div>
      </div>

      {/* Debug Info */}
      <div className="absolute top-1 left-1 text-white text-xs bg-black/60 px-2 py-1 rounded">
        <div>Viewport: {viewport.width}×{viewport.height} tiles</div>
        <div>Trees: {trees.length} total, {visibleTrees.length} visible</div>
        <div>Ghosts: {ghostUsers.length} total, {ghostUsers.filter(u => 
          u.position.x >= visibleTiles.startX && u.position.x <= visibleTiles.endX &&
          u.position.y >= visibleTiles.startY && u.position.y <= visibleTiles.endY
        ).length} visible</div>
      </div>
    </div>
  );
};

//Helper function->fallback tile colors
const getTileColor = (type: TileType): string => {
  switch (type) {
    case 'plantable': return '#22c55e';
    case 'walkable': return '#a3a3a3';
    case 'border': return '#525252';
    default: return '#000000';
  }
};

//Helper function -> tree fallbacks
//ToDo: Do these even make a difference? May end up deleting in prod if border works better
const getTreeFallbackColor = (maturityStage: number): string => {
  switch (maturityStage) {
    case 0: return '#8b5cf6'; //Seed - Purple
    case 1: return '#10b981'; //Sprout - Emerald
    case 2: return '#22c55e'; //Seedling - Green
    case 3: return '#16a34a'; //Sapling - Dark Green
    case 4: return '#15803d'; //Young - Darker Green
    case 5: return '#14532d'; //Mature - Darkest Green
    default: return '#059669';
  }
};

export default GameGrid;